<template>
  <div class="ci-summary-group">
    <div class="ci-summary-title ci-card-title ci-accent ci-accent--productivity">
      AVG Touch Counts
    </div>

    <div class="ci-summary-cards ci-scroll">
      <div v-if="loading" class="ci-summary-loading">Loading...</div>

      <template v-else>
        <button
          v-for="r in rows"
          :key="rowKey(r)"
          class="ci-summary-row ci-summary-row--click"
          type="button"
          @click="openModal(r)"
        >
          <div class="ci-summary-name" :title="r.underwriterDisplayName">
            {{ r.underwriterDisplayName }}
          </div>

          <div class="ci-summary-right">
            <div class="ci-summary-metric ci-summary-metric--muted">
              <span class="ci-pill-label ci-pill-label--muted">Avg</span>
              <span class="ci-pill-value">{{ fmtAvg(r.avgSubmittalCounter) }}</span>
            </div>

            <div class="ci-summary-metric">
              <span class="ci-pill-label ci-pill-label--muted">Loans</span>
              <span class="ci-pill-value">{{ r.totalRows }}</span>
            </div>
          </div>
        </button>

        <div v-if="!rows.length" class="ci-empty-inline">
          No activity in this range
        </div>
      </template>
    </div>

    <div v-if="showModal" class="ci-modal" @click.self="closeModal">
      <div class="ci-modal__panel">
        <div class="ci-modal__header">
          <div class="ci-modal__head-left">
            <div class="ci-modal__title">{{ selectedName }}</div>

            <div class="ci-modal__meta">
              <div class="ci-summary-metric ci-summary-metric--muted">
                <span class="ci-pill-label ci-pill-label--muted">Avg</span>
                <span class="ci-pill-value">{{ selectedAvg }}</span>
              </div>

              <div class="ci-summary-metric">
                <span class="ci-pill-label ci-pill-label--blue">Loans</span>
                <span class="ci-pill-value">{{ selectedLoans }}</span>
              </div>
            </div>
          </div>

          <button class="ci-modal__close" type="button" @click="closeModal">✕</button>
        </div>

        <div class="ci-modal__body ci-scroll">
          <div v-if="detailsLoading" class="ci-summary-loading">Loading details...</div>
          <div v-else-if="detailsError" class="ci-empty-inline ci-empty-inline--error">{{ detailsError }}</div>

          <template v-else>
            <div v-if="detailRows.length" class="ci-detail-list">
              <div v-for="d in detailRows" :key="detailKey(d)" class="ci-detail-card">
                <div class="ci-detail-head">
                  <div class="ci-detail-head-left">
                    <div class="ci-detail-col">
                      <div class="ci-detail-label">Loan</div>
                      <div class="ci-detail-value ci-mono" :title="String(d.f364 || '')">
                        {{ d.f364 }}
                      </div>
                    </div>

                    <div class="ci-detail-col">
                      <div class="ci-detail-label">Status</div>
                      <div class="ci-detail-value" :title="String(d.msstatus || '')">
                        {{ d.msstatus }}
                      </div>
                    </div>
                  </div>

                  <div class="ci-detail-chips">
                    <div class="ci-detail-chip">
                      <span class="ci-detail-chip-label">Submittal Date</span>
                      <span class="ci-detail-chip-value">
                        {{ fmtDate(d.logMSDateSubmittalDt || d.logMSDateSubmittal) }}
                      </span>
                    </div>

                    <div class="ci-detail-chip">
                      <span class="ci-detail-chip-label">Counter</span>
                      <span class="ci-detail-chip-value ci-mono">
                        {{ d.submittalCounter }}
                      </span>
                    </div>

                    <div class="ci-detail-chip">
                      <span class="ci-detail-chip-label">Processor</span>
                      <span class="ci-detail-chip-value" :title="String(d.f362 || '')">
                        {{ d.f362 }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="ci-empty-inline">No detail rows</div>
          </template>
        </div>

        <div class="ci-modal__footer">
          <button class="ci-btn" type="button" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"

export default {
  name: "InitialUwAvgSubmittalCounterSummaryCard",
  props: {
    authToken: { type: String, default: "" }
  },
  data() {
    return {
      loading: false,
      rows: [],

      showModal: false,
      selectedRow: null,
      detailsLoading: false,
      detailsError: "",
      detailRows: []
    }
  },
  computed: {
    selectedName() {
      return this.selectedRow?.underwriterDisplayName || ""
    },
    selectedAvg() {
      return this.fmtAvg(this.selectedRow?.avgSubmittalCounter)
    },
    selectedLoans() {
      return this.selectedRow?.totalRows ?? ""
    }
  },
  watch: {
    authToken: {
      immediate: true,
      handler() {
        if (!this.authToken) return
        this.fetchSummary()
      }
    }
  },
  methods: {
    rowKey(r) {
      return r.underwriterAzureId || r.underwriterResolvedEmail || r.underwriterEmail || r.underwriterDisplayName
    },
    detailKey(d) {
      return d.id || (d.f364 + "-" + d.submittalCounter + "-" + (d.logMSDateSubmittalDt || d.logMSDateSubmittal || ""))
    },
    emailOf(r) {
      return r?.underwriterResolvedEmail || r?.underwriterEmail || ""
    },
    fmtAvg(v) {
      const n = Number(v)
      if (Number.isNaN(n)) return v ?? ""
      return n.toFixed(2)
    },
    fmtDate(v) {
      if (!v) return ""
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      return d.toLocaleDateString()
    },
    async fetchSummary() {
      this.loading = true
      try {
        const resp = await ApiService.GetUwAvgSubmittalCounterRolling90Days(this.authToken)
        const data = resp?.data?.data || resp?.data || []
        this.rows = Array.isArray(data) ? data : []
      } finally {
        this.loading = false
      }
    },
    async openModal(r) {
      this.selectedRow = r
      this.showModal = true
      this.detailsError = ""
      this.detailRows = []
      await this.fetchDetails()
    },
    closeModal() {
      this.showModal = false
      this.selectedRow = null
      this.detailsLoading = false
      this.detailsError = ""
      this.detailRows = []
    },
    async fetchDetails() {
      const f1411 = this.emailOf(this.selectedRow)
      if (!f1411) {
        this.detailsError = "Missing underwriter email"
        return
      }

      this.detailsLoading = true
      try {
        const resp = await ApiService.GetUwSubmittalCounterDrillInRolling90Days(f1411, this.authToken)
        const data = resp?.data?.data || resp?.data || []
        this.detailRows = Array.isArray(data) ? data : []
      } catch (e) {
        this.detailsError = e?.message || "Failed to load details"
      } finally {
        this.detailsLoading = false
      }
    }
  }
}
</script>

<style scoped>
.ci-accent--productivity{ --accent-color:#2F80ED; }
.ci-accent--success{ --accent-color:#27AE60; }
.ci-accent--attention{ --accent-color:#F2994A; }
.ci-accent--insight{ --accent-color:#9B51E0; }

.ci-accent{
  border-left:10px solid var(--accent-color);
  border-top-left-radius:12px;
}

.ci-summary-group{
  min-height:0;
  height:100%;
  border:1px solid var(--border-ltr);
  border-radius:5px;
  background:var(--background);
  overflow:hidden;
  display:flex;
  flex-direction:column;
}

.ci-summary-title{
  padding:10px 12px;
  border-bottom:1px solid var(--accent-color);
  background:rgba(47,128,237,0.10);
  font-weight:950;
  font-size:12px;
  color:#2F80ED;
  flex:0 0 auto;
  display:flex;
  align-items:center;
  justify-content:space-between;
   height:60px;
}

.ci-summary-cards{
  flex:1 1 auto;
  min-height:0;
  padding:12px;
  display:flex;
  flex-direction:column;
  gap:10px;
}

.ci-scroll{overflow:auto}

.ci-summary-loading{
  height:74px;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:950;
  color:var(--text-secondary-on-background);
}

.ci-summary-row{
  width:100%;
  box-sizing:border-box;
  border:1px solid rgba(15,23,42,0.10);
  border-radius:5px;
  padding:10px 12px;
  background:rgba(15,23,42,0.015);
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  min-height:44px;
  text-align:left;
  font:inherit;
}

.ci-summary-row--click{
  cursor:pointer;
  outline:none;
}

.ci-summary-row--click:hover{
  background:rgba(15,23,42,0.03);
}

.ci-summary-name{
  font-size:12px;
  font-weight:950;
  color:var(--text-primary-on-background);
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
  flex:1 1 auto;
  min-width:0;
}

.ci-summary-right{
  flex:0 0 auto;
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap:10px;
}

.ci-summary-metric{
  display:inline-flex;
  align-items:baseline;
  gap:6px;
  white-space:nowrap;
}

.ci-pill-label{
  font-size:11px;
  font-weight:900;
  color:var(--text-secondary-on-background);
}

.ci-pill-label--muted{
  color:var(--text-secondary-on-background);
}

.ci-pill-label--blue{
  color:rgba(40,110,255,0.95);
}

.ci-pill-value{
  font-size:14px;
  font-weight:950;
  color:var(--text-primary-on-background);
  line-height:1;
}

.ci-empty-inline{
  padding:18px 10px;
  text-align:center;
  font-weight:950;
  color:var(--text-secondary-on-background);
}

.ci-empty-inline--error{
  color:rgba(180,0,0,0.80);
}

.ci-modal{
  position:fixed;
  inset:0;
  background:rgba(0,0,0,0.35);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:18px;
  z-index:9999;
}

.ci-modal__panel{
  width:min(980px, 100%);
  max-height:min(85vh, 900px);
  background:var(--background);
  border-radius:5px;
  border:1px solid var(--border-ltr);
  box-shadow:0 18px 60px rgba(0,0,0,0.25);
  overflow:hidden;
  display:flex;
  flex-direction:column;
}

.ci-modal__header{
  padding:12px;
  border-bottom:1px solid var(--border-ltr);
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  background:rgba(5, 29, 58, 0.03);
}

.ci-modal__head-left{min-width:0}

.ci-modal__title{
  font-weight:950;
  font-size:13px;
  color:var(--text-primary-on-background);
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.ci-modal__meta{
  margin-top:8px;
  display:flex;
  gap:10px;
  flex-wrap:wrap;
}

.ci-modal__close{
  width:34px;
  height:34px;
  border-radius:5px;
  border:1px solid rgba(15,23,42,0.12);
  background:rgba(15,23,42,0.02);
  cursor:pointer;
}

.ci-modal__close:hover{
  background:rgba(15,23,42,0.06);
}

.ci-modal__body{
  padding:12px;
  overflow:auto;
  min-height:0;
}

.ci-detail-list{
  display:flex;
  flex-direction:column;
  gap:10px;
}

.ci-detail-card{
  border:1px solid rgba(15,23,42,0.10);
  border-radius:5px;
  background:rgba(15,23,42,0.01);
  padding:10px 12px;
}

.ci-detail-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
}

.ci-detail-head-left{
  flex:1 1 auto;
  min-width:0;
  display:grid;
  grid-template-columns:180px 1fr;
  gap:12px;
  align-items:start;
}

.ci-detail-col{min-width:0}

.ci-detail-label{
  font-size:11px;
  font-weight:900;
  color:var(--text-secondary-on-background);
}

.ci-detail-value{
  font-size:12px;
  font-weight:950;
  color:var(--text-primary-on-background);
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.ci-detail-chips{
  flex:0 0 auto;
  margin-left:auto;
  display:flex;
  gap:8px;
  flex-wrap:wrap;
  justify-content:flex-end;
  align-items:flex-start;
}

.ci-detail-chip{
  display:inline-flex;
  align-items:baseline;
  gap:6px;
  padding:6px 10px;
  border-radius:5px;
  background:rgba(40,110,255,0.08);
  border:1px solid rgba(40,110,255,0.15);
}

.ci-detail-chip-label{
  font-size:11px;
  font-weight:900;
  color:var(--text-secondary-on-background);
}

.ci-detail-chip-value{
  font-size:12px;
  font-weight:950;
  color:var(--text-primary-on-background);
  line-height:1;
}

.ci-mono{
  font-variant-numeric:tabular-nums;
  font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;
}

.ci-modal__footer{
  padding:12px;
  border-top:1px solid var(--border-ltr);
  display:flex;
  justify-content:flex-end;
}

.ci-btn{
  border-radius:5px;
  padding:10px 12px;
  border:1px solid rgba(15,23,42,0.12);
  background:rgba(15,23,42,0.02);
  cursor:pointer;
  font-weight:950;
  font-size:12px;
}

.ci-btn:hover{
  background:rgba(15,23,42,0.06);
}
</style>
